import React, { useState } from 'react';
import { CheckCircleIcon, XCircleIcon } from '../icons';
import Modal from '../ui/Modal';
import { formatNavieraDiagnostics } from '../ui/NavieraDiagnostics';
import { themeColors } from '../../constants/design';
import { normalizeDestination } from '../../utils/destinations';
import questionnaire from '../../config/questionnaire';

const AdminSubmissionsReview = ({
  allSubmissions,
  onUpdateSubmission,
  onSetSubmissionExclusion,
  onDeleteSubmission,
  onPublishResults,
  onNotify,
}) => {
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [observations, setObservations] = useState({});
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  
  const submissions = allSubmissions.filter(s => s.month === monthFilter);
  const pendingCount = submissions.filter((s) => s.status === 'pending' && !s.excludedFromResults).length;
  const approvedCount = submissions.filter((s) => s.status === 'approved' && !s.excludedFromResults).length;
  const rejectedCount = submissions.filter((s) => s.status === 'rejected').length;
  const excludedCount = submissions.filter((s) => s.excludedFromResults).length;
  const summaryStats = [
    { label: 'Pendientes', value: pendingCount, helper: 'Por revisar', tone: 'text-amber-600' },
    { label: 'Aprobados', value: approvedCount, helper: 'Cuentan en el índice', tone: 'text-emerald-600' },
    { label: 'Rechazados', value: rejectedCount, helper: 'Requieren ajustes', tone: 'text-rose-600' },
    { label: 'Excluidos', value: excludedCount, helper: 'Pruebas o inválidos', tone: 'text-slate-500' },
  ];

  const handleSelectSubmission = (submission) => {
    setSelectedSubmission(submission);
    setObservations(submission.observations || {});
  };
  
  const handleObservationChange = (questionId, text) => {
    setObservations(prev => ({ ...prev, [questionId]: text }));
  };
  
  const handleUpdateSubmission = async (status) => {
    if (!selectedSubmission) return;
    
    try {
      await onUpdateSubmission(selectedSubmission.docId, status, observations);
      onNotify(`Envío ${status === 'approved' ? 'aprobado' : 'rechazado'} con observaciones.`, 'success');
      setSelectedSubmission(null);
      setObservations({});
    } catch (error) {
      onNotify(error.message || 'No se pudo actualizar el envío.', 'error');
    }
  };

  const handleToggleExclusion = async () => {
    if (!selectedSubmission) return;
    const nextExcluded = !selectedSubmission.excludedFromResults;
    try {
      await onSetSubmissionExclusion(selectedSubmission.docId, nextExcluded);
      onNotify(nextExcluded ? 'Envío excluido del cálculo del índice.' : 'Envío reincorporado al cálculo del índice.', 'success');
      setSelectedSubmission(null);
      setObservations({});
    } catch (error) {
      onNotify(error.message || 'No se pudo actualizar la exclusión del envío.', 'error');
    }
  };

  const handleDeleteSelectedSubmission = async () => {
    if (!selectedSubmission) return;
    const confirmed = window.confirm(`¿Eliminar definitivamente el envío de ${selectedSubmission.companyName}? Esta acción no se puede deshacer.`);
    if (!confirmed) return;
    try {
      await onDeleteSubmission(selectedSubmission.docId);
      onNotify('Envío eliminado definitivamente.', 'success');
      setSelectedSubmission(null);
      setObservations({});
    } catch (error) {
      onNotify(error.message || 'No se pudo eliminar el envío.', 'error');
    }
  };
  
  const allQuestions = questionnaire.getAllQuestions();
  
  // Calcular y Publicar Resultados
  const handlePublishResults = async () => {
    try {
      await onPublishResults(monthFilter);
    } catch (error) {
      onNotify(error.message || 'No se pudieron publicar los resultados.', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200/70 bg-white p-5 md:p-6 shadow-sm space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Panel de revisión</p>
            <h2 className="text-lg font-semibold text-slate-900">Envíos · {monthFilter}</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <input
              type="month"
              id="monthFilter"
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:border-[#004AAD] focus:ring-2 focus:ring-[#004AAD]/10 transition-colors"
            />
            <button
              onClick={handlePublishResults}
              className="w-full sm:w-auto px-4 py-2 rounded-xl text-white text-sm font-semibold bg-[#004AAD] hover:bg-[#003B8A] shadow-sm hover:shadow-md transition-all"
              title="Calcula y publica el índice global basado en TODOS los envíos aprobados del mes seleccionado."
            >
              Publicar {monthFilter}
            </button>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {summaryStats.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-slate-200/70 bg-slate-50/50 p-4">
              <p className="text-[0.65rem] uppercase text-slate-400">{stat.label}</p>
              <p className={`text-3xl font-semibold ${stat.tone}`}>{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.helper}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200/70 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Envíos del mes {monthFilter}</h3>
          <span className="text-xs text-slate-500">
            {submissions.length ? `${submissions.length} envíos · ${approvedCount} calculables` : 'Sin envíos'}
          </span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Empresa</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Sector</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Destino</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Estado</th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase text-slate-500">Acción</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {submissions.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-6 text-center text-slate-500">No hay envíos para este mes.</td>
                </tr>
              )}
              {submissions.map(sub => {
                const statusStyles = {
                  pending:  { cls: 'text-amber-700 bg-amber-50 border border-amber-200',  label: 'Pendiente' },
                  approved: { cls: 'text-emerald-700 bg-emerald-50 border border-emerald-200', label: 'Aprobado' },
                  rejected: { cls: 'text-rose-700 bg-rose-50 border border-rose-200',     label: 'Rechazado' },
                };
                return (
                  <tr key={sub.docId} className={`hover:bg-slate-50/60 transition-colors ${sub.excludedFromResults ? 'bg-slate-50/70 text-slate-500' : ''}`}>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-slate-900">{sub.companyName}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 text-sm">{sub.exportType}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 text-sm">{normalizeDestination(sub.destination)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1.5">
                        <span className={`px-2.5 py-1 rounded-md font-medium text-xs ${statusStyles[sub.status]?.cls}`}>
                          {statusStyles[sub.status]?.label ?? sub.status}
                        </span>
                        {sub.excludedFromResults && (
                          <span className="px-2.5 py-1 rounded-md border border-slate-200 bg-slate-100 text-xs font-semibold text-slate-600">
                            Excluido
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => handleSelectSubmission(sub)}
                        className="px-3 py-1.5 rounded-xl text-sm font-semibold text-[#004AAD] border border-[#004AAD]/20 hover:bg-[#004AAD]/5 transition"
                      >
                        Revisar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modal de Revisión */}
      <Modal isOpen={!!selectedSubmission} onClose={() => setSelectedSubmission(null)} title={`Revisión: ${selectedSubmission?.companyName}`}>
        <div className="space-y-4">
          {selectedSubmission && (
            <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200/70 text-sm text-slate-600 space-y-1">
              <p><span className="font-semibold text-slate-700">Sector:</span> {selectedSubmission.exportType}</p>
              <p><span className="font-semibold text-slate-700">Destino:</span> {normalizeDestination(selectedSubmission.destination)}</p>
              <p><span className="font-semibold text-slate-700">Contacto:</span> {selectedSubmission.contactEmail || 'No registrado'}</p>
              <p><span className="font-semibold text-slate-700">Tendencia de costos:</span> {selectedSubmission.trend || 'No reportado'}</p>
              <p><span className="font-semibold text-slate-700">Percepción de rentabilidad:</span> {selectedSubmission.rentabilidadTrend || 'Sin dato'}</p>
              {selectedSubmission.excludedFromResults && (
                <p><span className="font-semibold text-slate-700">Cálculo:</span> Excluido del índice {selectedSubmission.exclusionReason ? `(${selectedSubmission.exclusionReason})` : ''}</p>
              )}
            </div>
          )}
          {allQuestions.map(q => (
            <div key={q.id} className="p-4 bg-slate-50/70 rounded-xl border border-slate-200/70">
              <p className="font-medium text-sm text-slate-700">{q.text}</p>
              <p className={`${themeColors.primary.text} whitespace-pre-line font-semibold mt-1`}>
                {q.type === 'naviera_diagnostics'
                  ? formatNavieraDiagnostics(selectedSubmission?.data[q.id])
                  : selectedSubmission?.data[q.id] || '(Sin respuesta)'}
              </p>
              
              {/* Campo de Observación */}
              <div className="mt-2">
                <label className="block text-xs font-medium text-slate-500">Observación (para esta pregunta)</label>
                <input
                  type="text"
                  value={observations[q.id] || ''}
                  onChange={(e) => handleObservationChange(q.id, e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-700 shadow-none placeholder:text-slate-300 focus:border-[#004AAD] focus:outline-none focus:ring-2 focus:ring-[#004AAD]/10"
                  placeholder="Ej. Por favor, revise este costo."
                />
              </div>
            </div>
          ))}
          {selectedSubmission?.attachments && Object.keys(selectedSubmission.attachments).length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Adjuntos</h3>
              <ul className="list-disc pl-5 text-sm text-gray-600">
                {Object.entries(selectedSubmission.attachments).map(([dim, file]) => (
                  <li key={dim}>{questionnaire.getDimensions().find(d => d.id === dim)?.name || dim}: {file.name}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-col gap-3 mt-6 border-t pt-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                onClick={handleDeleteSelectedSubmission}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-rose-700 border border-rose-200 hover:bg-rose-50 transition-colors"
              >
                Eliminar
              </button>
              <button
                onClick={handleToggleExclusion}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                {selectedSubmission?.excludedFromResults ? 'Incluir en índice' : 'Excluir del índice'}
              </button>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => handleUpdateSubmission('rejected')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors"
              >
                <XCircleIcon className="h-4 w-4" /> Rechazar
              </button>
              <button
                onClick={() => handleUpdateSubmission('approved')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#004AAD] hover:bg-[#003B8A] transition-colors shadow-sm"
              >
                <CheckCircleIcon className="h-4 w-4" /> Aprobar
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminSubmissionsReview;
